import {
  ApiError,
  createSetResponseSchema,
  entitlementUsageSchema,
  jobSchema,
  setDetailSchema,
  sourceListResponseSchema,
  sourceSchema,
  type CreateSetRequest,
  type PasteTextRequest,
  type setPatchSchema,
  pricingConfigResponseSchema,
  profileSchema,
  programmesResponseSchema,
  progressSummarySchema,
  setListResponseSchema,
  coreLibraryResetResponseSchema,
  coreLibraryStatusSchema,
  type OnboardingRequest,
  type Profile,
  type ProfilePatch,
} from "@studyward/shared";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "./api";
import { useSession } from "./session";

export const keys = {
  profile: ["profile"] as const,
  programmes: ["programmes"] as const,
  pricing: ["pricing"] as const,
  usage: ["usage"] as const,
  progress: ["progress", "summary"] as const,
  core: ["core-library"] as const,
  sets: ["sets"] as const,
  set: (id: string) => ["sets", id] as const,
  sources: ["sources"] as const,
  job: (id: string) => ["jobs", id] as const,
};

function useSignedIn() {
  return useSession().status === "signed_in";
}

export function useProfile() {
  return useQuery({
    queryKey: keys.profile,
    queryFn: () => api.get("/profile", profileSchema),
    enabled: useSignedIn(),
    retry: (failureCount, error) => !(error instanceof ApiError && error.code === "unauthorized") && failureCount < 3,
  });
}

export function useProgrammes() {
  return useQuery({
    queryKey: keys.programmes,
    queryFn: async () => (await api.get("/programmes", programmesResponseSchema)).items,
    staleTime: 5 * 60_000,
  });
}

/** Prices, limits and features always come from the server (spec §0.2, §9) — never hard-coded. */
export function usePricing() {
  return useQuery({ queryKey: keys.pricing, queryFn: () => api.get("/config/pricing", pricingConfigResponseSchema), staleTime: 5 * 60_000 });
}

/** This month's plan + usage, so limit states show real numbers (spec §8.4). */
export function useUsage() {
  return useQuery({ queryKey: keys.usage, queryFn: () => api.get("/billing/entitlement", entitlementUsageSchema), enabled: useSignedIn() });
}

export function useProgress() {
  return useQuery({ queryKey: keys.progress, queryFn: () => api.get("/progress/summary", progressSummarySchema), enabled: useSignedIn() });
}

export function useSets() {
  return useQuery({ queryKey: keys.sets, queryFn: () => api.get("/sets", setListResponseSchema), enabled: useSignedIn() });
}

function useProfileWrite<T>(fn: (input: T) => Promise<Profile>) {
  const queryClient = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: (profile) => queryClient.setQueryData(keys.profile, profile) });
}

export const useCompleteOnboarding = () =>
  useProfileWrite((body: OnboardingRequest) => api.post("/profile/onboarding", profileSchema, body));

export const useUpdateProfile = () => useProfileWrite((patch: ProfilePatch) => api.patch("/profile", profileSchema, patch));

const okSchema = z.object({ ok: z.literal(true) });

/** Library pages (newest first); `archived` lists the archive instead. */
export function useSetPages(archived: boolean) {
  return useInfiniteQuery({
    queryKey: [...keys.sets, { archived }],
    queryFn: ({ pageParam }) =>
      api.get(`/sets?${new URLSearchParams({ ...(archived ? { archived: "1" } : {}), ...(pageParam ? { cursor: pageParam } : {}) })}`, setListResponseSchema),
    initialPageParam: "",
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: useSignedIn(),
  });
}

export function useSet(id: string, opts: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: keys.set(id),
    queryFn: () => api.get(`/sets/${id}`, setDetailSchema),
    enabled: useSignedIn() && !!id,
    refetchInterval: (q) => (opts.poll && (q.state.data?.status === "generating" || q.state.data?.status === "draft") ? 4000 : false),
  });
}

/** Polls a generation job every 2 s until it finishes; each poll also nudges the worker along (§5.4). */
export function useJob(id: string | null) {
  return useQuery({
    queryKey: keys.job(id ?? ""),
    queryFn: () => api.get(`/jobs/${id}`, jobSchema),
    enabled: useSignedIn() && !!id,
    refetchInterval: (q) => (q.state.data?.status === "succeeded" || q.state.data?.status === "failed" ? false : 2000),
  });
}

/** Uploads waiting to be built into a set ("Ready to generate"). */
export function useReadySources() {
  return useQuery({ queryKey: keys.sources, queryFn: async () => (await api.get("/sources", sourceListResponseSchema)).items, enabled: useSignedIn() });
}

function useContentMutation<TIn, TOut>(fn: (input: TIn) => Promise<TOut>, invalidate: ReadonlyArray<readonly unknown[]>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => Promise.all(invalidate.map((queryKey) => queryClient.invalidateQueries({ queryKey }))),
  });
}

export const usePasteText = () => useContentMutation((body: PasteTextRequest) => api.post("/sources/text", sourceSchema, body), [keys.sources]);
export const useRemoveSource = () => useContentMutation((id: string) => api.delete(`/sources/${id}`, okSchema), [keys.sources]);
export const useCreateSet = () =>
  useContentMutation((body: CreateSetRequest) => api.post("/sets", createSetResponseSchema, body), [keys.sources, keys.sets, keys.usage]);
export const useRegenerateSet = () =>
  useContentMutation(
    ({ setId, idempotency_key }: { setId: string; idempotency_key: string }) => api.post(`/sets/${setId}/regenerate`, createSetResponseSchema, { idempotency_key }),
    [keys.sets, keys.usage],
  );
export const useUpdateSet = () =>
  useContentMutation(({ id, patch }: { id: string; patch: z.input<typeof setPatchSchema> }) => api.patch(`/sets/${id}`, okSchema, patch), [keys.sets]);
export const useDeleteSet = () => useContentMutation((id: string) => api.delete(`/sets/${id}`, okSchema), [keys.sets, keys.usage]);

/** One key per user action: a double click or a retried request never creates (or charges) a second set. */
export function newIdempotencyKey(): string {
  return `${Date.now().toString(36)}-${crypto.randomUUID()}`;
}

/** StudyWard Core library status for this student (OD-1): available once released sets exist, plus their choice. */
export function useCoreLibrary() {
  return useQuery({ queryKey: keys.core, queryFn: () => api.get("/core-library", coreLibraryStatusSchema), enabled: useSignedIn() });
}

/** Turn the Core library on or off; off hides it, nothing is deleted. */
export function useSetCoreLibrary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => api.put("/core-library", coreLibraryStatusSchema, { enabled }),
    onSuccess: (status) => {
      queryClient.setQueryData(keys.core, status);
      void queryClient.invalidateQueries({ queryKey: keys.sets });
    },
  });
}

/** Clear this student's Core review schedule; their own sets are never touched. */
export function useResetCoreLibrary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/core-library/reset", coreLibraryResetResponseSchema, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.core });
      void queryClient.invalidateQueries({ queryKey: keys.progress });
    },
  });
}
